import type { Attachment } from "@/api/attachment";
import { useSections } from "@/api/cms";
import { useAuth } from "@/context/auth/AuthContext";
import { getMainLanguage } from "@/i18n";
import type { CatalogueRaisonneSection } from "@/lib/cms/cms-types";
import type { BlockStyle } from "@/lib/cms-puck/block-style";
import type { CatalogueDecoupageListItem } from "@/lib/cms-puck/blocks/catalogue-decoupages";
import { normalizeCaptionAlign } from "@/lib/cms-puck/blocks/selection";
import { useBlockTextCss } from "@/hooks/use-block-style-css";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useCatalogFlowPreview } from "@/context/cms-puck/CatalogFlowPreviewContext";
import { useMemo } from "react";

type SectionWithMedia = CatalogueRaisonneSection & {
  attachment?: Attachment | null;
  vignette?: Attachment | null;
};

const NAME_ALIGN_CLASS: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/** KEEP IN SYNC avec `PuckCatalogDecoupages` (front). */
const COLUMNS_CLASS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-2 lg:grid-cols-3",
  4: "md:grid-cols-2 lg:grid-cols-4",
};

function getVignetteDisplayUrl(vignette: Attachment): string | undefined {
  if (vignette.kind === "video" || vignette.kind === "embed") {
    return (
      vignette.posterPublicUrl ??
      vignette.mediumPublicUrl ??
      vignette.publicUrl ??
      vignette.largePublicUrl
    );
  }
  return (
    vignette.mediumPublicUrl ??
    vignette.publicUrl ??
    vignette.largePublicUrl ??
    vignette.smallPublicUrl
  );
}

/**
 * Aperçu canvas du bloc « Découpages » — même markup que le front public /
 * ancien hub CR (cartes bordées sans vignette, grille gap-10).
 * Si `items` non vide → ordre/filtre local ; sinon legacy (toutes les sections).
 */
export function CatalogueDecoupagesPreview({
  target: targetProp = "ARTWORKS",
  columns = 4,
  showSectionName = true,
  nameAlign = "center",
  items,
  style,
}: {
  target?: "ARTWORKS" | "MULTIPRINTS";
  columns?: 2 | 3 | 4;
  showSectionName?: boolean;
  nameAlign?: string;
  items?: CatalogueDecoupageListItem[];
  style?: BlockStyle;
}) {
  const vt = useVisibleT();
  const flow = useCatalogFlowPreview();
  const target =
    flow?.target === "ARTWORKS" || flow?.target === "MULTIPRINTS"
      ? flow.target
      : targetProp;
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id ?? "";
  const mainLang = getMainLanguage();
  const captionTextCss = useBlockTextCss(style);
  const alignClass = NAME_ALIGN_CLASS[normalizeCaptionAlign(nameAlign)];
  const cols = COLUMNS_CLASS[columns] ?? COLUMNS_CLASS[4];

  const { data: sections = [], isLoading } = useSections(cmsId, target);

  const displaySections = useMemo(() => {
    if (!items?.length) return sections;
    const byId = new Map(sections.map((s) => [s.id, s]));
    const ordered: CatalogueRaisonneSection[] = [];
    for (const it of items) {
      const s = byId.get(it.sectionId);
      if (s) ordered.push(s);
    }
    return ordered;
  }, [items, sections]);

  if (isLoading) {
    return (
      <div className="w-full py-6 text-center text-sm cms-page-text-muted">
        {vt("cms.new.preview.catalogueDecoupages.loading", "Chargement…")}
      </div>
    );
  }

  if (displaySections.length === 0) {
    return (
      <div className="flex w-full items-center justify-center rounded border border-dashed border-slate-300 py-10 text-sm text-slate-500">
        {vt(
          "cms.new.preview.catalogueDecoupages.empty",
          items?.length
            ? "Aucun découpage de la liste n'est disponible."
            : "Aucun découpage — créez-en dans l'éditeur du bloc.",
        )}
      </div>
    );
  }

  return (
    <div className={`grid w-full grid-cols-1 gap-10 ${cols}`}>
      {displaySections.map((section) => {
        const names = (section.nameMultilingual ?? {}) as Record<
          string,
          string
        >;
        const name =
          names[mainLang] || Object.values(names).find(Boolean) || "";
        const withMedia = section as SectionWithMedia;
        const vignette = withMedia.attachment ?? withMedia.vignette ?? null;
        const url = vignette ? getVignetteDisplayUrl(vignette) : undefined;
        const hasMedia = Boolean(url);

        if (hasMedia) {
          return (
            <div key={section.id} className="flex flex-col gap-2">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={url}
                alt={name}
                className="h-auto w-full object-contain"
                style={{ maxWidth: "100%" }}
              />
              {showSectionName ? (
                <span
                  className={`text-2xl font-semibold ${alignClass}`}
                  style={captionTextCss}
                >
                  {name || "—"}
                </span>
              ) : null}
            </div>
          );
        }

        return (
          <div
            key={section.id}
            className="border cms-page-border rounded-lg p-2 h-fit"
          >
            <span
              className={`text-xl font-semibold ${alignClass}`}
              style={captionTextCss}
            >
              {showSectionName ? name || "—" : "—"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
